import { useEffect } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { z } from "zod";
import { Trans } from "@lingui/react/macro";
import { useAuth } from "@/hooks/useAuth";
import { setImpersonationContext } from "@/lib/auth/impersonation";

const impersonateSearchSchema = z.object({
  loggedin_as: z.string().optional(),
  organization: z.string().optional(),
  concept: z.string().optional(),
});

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const Route = createFileRoute("/(protected)/_layout/impersonate")({
  validateSearch: (search) => impersonateSearchSchema.parse(search),
  component: ImpersonateBootstrapPage,
});

function ImpersonateBootstrapPage() {
  const navigate = useNavigate();
  const { hasRole, isLoading, refetchUser } = useAuth();
  const { loggedin_as, organization, concept } = Route.useSearch();

  const canImpersonate =
    hasRole("SUPER_ADMIN") || hasRole("SUPPORT") || hasRole("ORG_ADMIN");

  useEffect(() => {
    if (isLoading) {
      return;
    }

    if (!canImpersonate) {
      void navigate({
        to: "/unauthorized",
        search: { reason: "impersonation_role_denied", from: "/impersonate" },
        replace: true,
      });
      return;
    }

    const normalizedEmail = loggedin_as?.trim().toLowerCase();
    if (!normalizedEmail || !EMAIL_PATTERN.test(normalizedEmail)) {
      void navigate({
        to: "/unauthorized",
        search: { reason: "invalid_impersonation_target", from: "/impersonate" },
        replace: true,
      });
      return;
    }

    setImpersonationContext({
      email: normalizedEmail,
      organizationName: organization,
      conceptName: concept,
    });
    void (async () => {
      await refetchUser();
      await navigate({ to: "/dashboard", replace: true });
    })();
  }, [canImpersonate, concept, isLoading, loggedin_as, navigate, organization, refetchUser]);

  return (
    <div className="flex flex-1 items-center justify-center p-8">
      <div className="text-(--color-text-secondary)">
        <Trans>Starting impersonation...</Trans>
      </div>
    </div>
  );
}
